import { SceneInsight } from "@/api/studio/insights/types/insight.type";
import { Tabs, Tab, TabPanel } from "@/components/ui/tabs";
import { useReportsSceneStore, SceneMetric } from "../../stores/use-reports-scene-store";
import { useSegment } from "@/hooks/use-segment";
import { SceneRegionTable } from "./scene-region-table";
import { SceneMetricHeader } from "./scene-metric-header";

/* Prop Types */
interface SceneMetricsDetailsProps {
  currentScene: SceneInsight;
  studyId: string;
  projectId: string;
}

/*
 * SceneMetricsDetails component
 *
 * This component displays detailed information about the scene metrics (vis, pop, soa, pow)
 * of a given scene for the in-depth report.
 */
export const SceneMetricsDetails = ({ currentScene, studyId, projectId }: SceneMetricsDetailsProps) => {
  /* Store */
  const metric = useReportsSceneStore((state) => state.metric);
  const setMetric = useReportsSceneStore((state) => state.setMetric);

  /* Consts */
  const regions = currentScene?.attention.regions || [];

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Handlers */
  const handleMetricChange = (newMetric: SceneMetric) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Viewed", {
      assetId: currentScene.key,
      projectId,
      studyId,
      reportType: metric,
      studyType: "scene",
    });

    // set the new metric
    setMetric(newMetric);
  };

  const handleReadMoreClick = () => {
    // Segment Tracking
    segmentTrackEvent("Scene In-depth Tooltip Viewed", {
      assetId: currentScene.key,
      projectId,
      studyId,
      metric,
      studyType: "scene",
    });
  };

  /* Render */
  return (
    <div className="flex flex-col min-w-[28vw] max-w-[28vw] overflow-y-auto pt-4" style={{ scrollbarGutter: "stable" }}>
      <Tabs
        defaultTab={metric}
        onTabChange={(tab) => {
          handleMetricChange(tab as SceneMetric);
        }}
        showActiveBg
      >
        {/* Visibility Tab */}
        <Tab value="vis" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
          Visibility
        </Tab>

        {/* POP Tab */}
        <Tab value="pop" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
          POP
        </Tab>

        {/* SOA Tab */}
        <Tab value="soa" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
          SOA
        </Tab>

        {/* POW Tab */}
        <Tab value="pow" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
          POW
        </Tab>

        {/* Visibility Panel */}
        <TabPanel value="vis">
          <SceneMetricHeader metric="vis" onReadMoreClick={handleReadMoreClick} />
          <SceneRegionTable regions={regions} metric="vis" />
        </TabPanel>

        {/* POP Panel */}
        <TabPanel value="pop" className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0">
          <SceneMetricHeader metric="pop" onReadMoreClick={handleReadMoreClick} />
          <SceneRegionTable regions={regions} metric="pop" />
        </TabPanel>

        {/* SOA Panel */}
        <TabPanel value="soa" className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0">
          <SceneMetricHeader metric="soa" onReadMoreClick={handleReadMoreClick} />
          <SceneRegionTable regions={regions} metric="soa" />
        </TabPanel>

        {/* POW Panel */}
        <TabPanel value="pow" className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0">
          <SceneMetricHeader metric="pow" onReadMoreClick={handleReadMoreClick} />
          <SceneRegionTable regions={regions} metric="pow" />
        </TabPanel>
      </Tabs>
    </div>
  );
};
